import React from 'react';
import DOMPurify from 'dompurify';
import startCase from 'lodash/startCase';
import styled from 'styled-components';

import SvgIcon from '@joinforma/ui/components/SvgIcon';
import Body1 from '@joinforma/ui/components/Body1';
import Title2 from '@joinforma/ui/components/Title2';
import { COLORS } from '@joinforma/ui/constants/legacy/theme';

import { ReactComponent as CheckSvg } from '@joinforma/ui/assets/icons/check-circle.svg';
import { ReactComponent as CrossSvg } from '@joinforma/ui/assets/icons/cancel.svg';
import { ReactComponent as TimerSvg } from '@joinforma/ui/assets/icons/timer.svg';
import { ReactComponent as RecurringSvg } from 'assets/images/recurring.svg';
import { ReactComponent as DefaultSvg } from '@joinforma/ui/assets/icons/legacy/outline-minus.svg';
import {
  SVG_ICON_COLORS,
  SVG_ICON_SIZES,
} from '@joinforma/ui/constants/properties';

import { CLAIM_NEW_PAGE_ELEMENTS } from '../../constants/testSelectors';

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const getTextColor = ({
  primary,
  positive,
  warning,
  negative,
  status,
}: $TSFixMe) => {
  switch (status) {
    case 'positive':
      return positive;
    case 'warning':
      return warning;
    case 'negative':
      return negative;
    default:
      return primary;
  }
};

const TextSmall = styled(Title2)`
  color: ${({
    color,
    theme: {
      colors: {
        icon: { primary, positive, warning, negative },
      },
    },
  }) => getTextColor({ status: color, primary, positive, negative, warning })};
`;

const TextMedium = styled(Body1)`
  display: block;
  color: ${({
    color,
    theme: {
      colors: {
        icon: { positive, warning, negative },
      },
    },
  }) => getTextColor({ status: color, positive, negative, warning })};
`;

const STATUS = {
  approved: {
    color: SVG_ICON_COLORS.POSITIVE,
    icon: CheckSvg,
  },
  active: {
    color: SVG_ICON_COLORS.POSITIVE,
    icon: CheckSvg,
  },
  completed: {
    color: SVG_ICON_COLORS.POSITIVE,
    icon: CheckSvg,
  },
  confirmed: {
    color: SVG_ICON_COLORS.POSITIVE,
    icon: CheckSvg,
  },
  fulfilled: {
    color: SVG_ICON_COLORS.POSITIVE,
    icon: CheckSvg,
  },
  shipped: {
    color: SVG_ICON_COLORS.POSITIVE,
    icon: CheckSvg,
  },
  in_progress: {
    color: SVG_ICON_COLORS.POSITIVE,
    text: 'recurring',
    icon: RecurringSvg,
  },
  rejected: {
    color: SVG_ICON_COLORS.NEGATIVE,
    icon: CrossSvg,
  },
  cancelled: {
    color: SVG_ICON_COLORS.NEGATIVE,
    icon: CrossSvg,
  },
  declined: {
    color: SVG_ICON_COLORS.NEGATIVE,
    icon: CrossSvg,
  },
  denied: {
    color: SVG_ICON_COLORS.NEGATIVE,
    icon: CrossSvg,
  },
  pending: {
    color: SVG_ICON_COLORS.WARNING,
    icon: TimerSvg,
  },
  processing: {
    color: SVG_ICON_COLORS.WARNING,
    icon: TimerSvg,
  },
  pending_cancel: {
    color: SVG_ICON_COLORS.WARNING,
    icon: TimerSvg,
  },
  default: {
    color: COLORS.BLACK_50,
    icon: DefaultSvg,
  },
  // Pretax
  'Entered<br>Not Reviewed': {
    text: 'Pending',
    color: SVG_ICON_COLORS.WARNING,
    icon: TimerSvg,
  },
  'Reviewed<br>Pending Approval': {
    text: 'Pending',
    color: SVG_ICON_COLORS.WARNING,
    icon: TimerSvg,
  },
  'needs receipt': {
    color: SVG_ICON_COLORS.NEGATIVE,
    icon: CrossSvg,
  },
  paid: {
    color: SVG_ICON_COLORS.POSITIVE,
    icon: CheckSvg,
  },
};

const StatusIcon = styled(SvgIcon)`
  margin-right: 5px;
`;

const TransactionStatus = ({ status, show_icon, size }: $TSFixMe) => {
  const config =
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    STATUS[status] || STATUS[status.toLowerCase()] || STATUS.default;

  return (
    <Container data-testid={CLAIM_NEW_PAGE_ELEMENTS.STATUS}>
      {show_icon && (
        <StatusIcon
          icon={config.icon}
          size={SVG_ICON_SIZES.SMALL}
          fill={config.color}
        />
      )}

      {size === 'small' && (
        <TextSmall color={config.color}>
          {DOMPurify.sanitize(startCase(config.text || status))}
        </TextSmall>
      )}

      {size === 'medium' && (
        <TextMedium color={config.color}>
          {DOMPurify.sanitize(startCase(config.text || status))}
        </TextMedium>
      )}
    </Container>
  );
};

export default TransactionStatus;
